import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPreview } from '../../contexts/AdminPreviewContext';
import Breadcrumbs from '../shared/Breadcrumbs';
import helpSections from './helpContent';

/**
 * HelpCenter Component
 * 
 * Comprehensive, role-aware Help Center / Knowledge Base.
 * Filters content based on the user's role so Agents only see
 * content relevant to them, Team Leads see their scope, and
 * Admins see everything.
 */

const ROLE_HIERARCHY = {
  basic: ['basic'],
  team_lead: ['team_lead'],   // Team Leads see team_lead + ALL_ROLES articles, NOT basic-only ones
  admin: ['basic', 'team_lead', 'admin']  // Admins see everything
};

const HelpCenter = () => {
  useAuth(); // keep auth context subscription
  const { effectiveUserRole: userRole } = useAdminPreview();
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const searchInputRef = useRef(null);
  const articleContentRef = useRef(null);

  const allowedRoles = ROLE_HIERARCHY[userRole] || ROLE_HIERARCHY.basic;

  // Filter sections and articles based on user role
  const filteredSections = useMemo(() => {
    return helpSections
      .filter(section => section.roles.some(r => allowedRoles.includes(r)))
      .map(section => ({
        ...section,
        articles: section.articles.filter(article =>
          article.roles.some(r => allowedRoles.includes(r))
        )
      }))
      .filter(section => section.articles.length > 0);
  }, [allowedRoles]);

  // Search filtering
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase().trim();
    const results = [];

    filteredSections.forEach(section => {
      section.articles.forEach(article => {
        const titleMatch = article.title.toLowerCase().includes(query);
        const keywordMatch = article.keywords?.some(k => k.toLowerCase().includes(query));
        const contentMatch = article.content?.some(block => {
          if (block.type === 'paragraph' || block.type === 'heading') {
            return block.text.toLowerCase().includes(query);
          }
          if (block.type === 'list') {
            return block.items.some(item => item.toLowerCase().includes(query));
          }
          return false;
        });

        if (titleMatch || keywordMatch || contentMatch) {
          results.push({
            ...article,
            sectionId: section.id,
            sectionTitle: section.title,
            sectionIcon: section.icon,
            relevance: titleMatch ? 3 : keywordMatch ? 2 : 1
          });
        }
      });
    });

    return results.sort((a, b) => b.relevance - a.relevance);
  }, [searchQuery, filteredSections]);

  // Initialize with first section selected
  useEffect(() => {
    if (filteredSections.length > 0 && !selectedSection) {
      setSelectedSection(filteredSections[0].id);
    }
  }, [filteredSections, selectedSection]);

  // Scroll article content to top when article changes
  useEffect(() => {
    if (articleContentRef.current) {
      articleContentRef.current.scrollTop = 0;
    }
  }, [selectedArticle]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const selectArticle = useCallback((sectionId, articleId) => {
    setSelectedSection(sectionId);
    setSelectedArticle(articleId);
    setSearchQuery('');
  }, []);

  const handleSearchSelect = useCallback((article) => {
    setSelectedSection(article.sectionId);
    setSelectedArticle(article.id);
    setSearchQuery('');
    setExpandedSections(prev => ({ ...prev, [article.sectionId]: true }));
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  // Get current article for display
  const currentArticle = useMemo(() => {
    if (!selectedSection || !selectedArticle) return null;
    const section = filteredSections.find(s => s.id === selectedSection);
    if (!section) return null;
    return section.articles.find(a => a.id === selectedArticle);
  }, [selectedSection, selectedArticle, filteredSections]);

  const currentSection = useMemo(() => {
    return filteredSections.find(s => s.id === selectedSection);
  }, [selectedSection, filteredSections]);

  // Render article content blocks
  const renderContent = useCallback((blocks) => {
    return blocks.map((block, idx) => {
      switch (block.type) {
        case 'heading':
          return (
            <h4
              key={idx}
              className="text-base font-semibold text-gray-900 mt-4 mb-2"
            >
              {block.text}
            </h4>
          );
        case 'paragraph':
          return (
            <p
              key={idx}
              className="text-sm text-gray-700 leading-relaxed mb-3"
            >
              {block.text}
            </p>
          );
        case 'list':
          return (
            <ul
              key={idx}
              className="list-disc list-inside space-y-1.5 mb-3 text-sm text-gray-700"
            >
              {block.items.map((item, i) => (
                <li key={i} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          );
        case 'tip':
          return (
            <div
              key={idx}
              className="flex gap-2.5 bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-3"
              role="note"
            >
              <span className="text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </span>
              <div className="text-sm text-emerald-800 leading-relaxed">
                <span className="font-semibold">Tip: </span>{block.text}
              </div>
            </div>
          );
        case 'warning':
          return (
            <div
              key={idx}
              className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3"
              role="alert"
            >
              <span className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </span>
              <div className="text-sm text-amber-800 leading-relaxed">
                <span className="font-semibold">Important: </span>{block.text}
              </div>
            </div>
          );
        case 'note':
          return (
            <div
              key={idx}
              className="flex gap-2.5 bg-blue-50 border border-blue-200 rounded-md p-3 mb-3"
              role="note"
            >
              <span className="text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <div className="text-sm text-blue-800 leading-relaxed">
                {block.text}
              </div>
            </div>
          );
        case 'steps':
          return (
            <div key={idx} className="mb-4">
              {block.items.map((step, i) => (
                <div key={i} className="flex gap-3 mb-0.5 last:mb-0">
                  <div className="flex flex-col items-center">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    {i < block.items.length - 1 && (
                      <span className="w-px flex-1 bg-blue-200 my-0.5" />
                    )}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed pb-3">{step}</p>
                </div>
              ))}
            </div>
          );
        case 'image':
          return (
            <figure key={idx} className="mb-4">
              <div className="border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                <img
                  src={block.src}
                  alt={block.alt || ''}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              {block.caption && (
                <figcaption className="text-xs text-gray-500 mt-1.5 text-center italic">
                  {block.caption}
                </figcaption>
              )}
            </figure>
          );
        case 'relatedArticles': {
          const accessibleLinks = block.links.filter(link => {
            const section = filteredSections.find(s => s.id === link.sectionId);
            return section && section.articles.some(a => a.id === link.articleId);
          });
          if (accessibleLinks.length === 0) return null;
          return (
            <div
              key={idx}
              className="mt-4 pt-3 border-t border-gray-200"
            >
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Related Articles
              </p>
              <div className="flex flex-wrap gap-2">
                {accessibleLinks.map((link, i) => (
                  <button
                    key={i}
                    onClick={() => selectArticle(link.sectionId, link.articleId)}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-md transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {link.title}
                  </button>
                ))}
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    });
  }, [selectArticle, filteredSections]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Page Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 h-14 bg-white flex-shrink-0">
        <h2 className="text-xl font-semibold text-blue-600 border-b-2 border-blue-600 pb-1">
          Help Center
        </h2>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white px-4 pt-1.5 flex-shrink-0">
        <Breadcrumbs items={[
          { label: 'Home', href: '/call-script' },
          { label: 'Help Center', href: '/help-center' },
          ...(currentSection && currentArticle ? [
            { label: currentSection.title, onClick: () => setSelectedArticle(currentSection.articles[0]?.id || null) },
            { label: currentArticle.title, current: true }
          ] : currentSection ? [
            { label: currentSection.title, current: true }
          ] : [])
        ]} />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden bg-gray-50">
        {/* Left sidebar - section/article navigation */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          {/* Search bar */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search help articles..."
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Search help articles"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Search results */}
          {searchResults ? (
            <div className="flex-1 overflow-y-auto p-2">
              {searchResults.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">No results found</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-gray-400 px-2 mb-2">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  </p>
                  {searchResults.map(result => (
                    <button
                      key={`${result.sectionId}-${result.id}`}
                      onClick={() => handleSearchSelect(result)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-800">{result.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {result.sectionIcon} {result.sectionTitle}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Section navigation */
            <nav className="flex-1 overflow-y-auto p-2" aria-label="Help Center sections">
              {filteredSections.map(section => {
                const isExpanded = expandedSections[section.id] ?? (section.id === selectedSection);
                const isActive = section.id === selectedSection;

                return (
                  <div key={section.id} className="mb-1">
                    {/* Section header */}
                    <button
                      onClick={() => {
                        setSelectedSection(section.id);
                        setSelectedArticle(section.articles[0]?.id || null);
                        setExpandedSections(prev => ({ ...prev, [section.id]: true }));
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      aria-expanded={isExpanded}
                    >
                      <span className="flex items-center gap-2">
                        <span>{section.icon}</span>
                        <span>{section.title}</span>
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    {/* Article list */}
                    {isExpanded && (
                      <div className="ml-4 mt-0.5 space-y-0.5">
                        {section.articles.map(article => (
                          <button
                            key={article.id}
                            onClick={() => selectArticle(section.id, article.id)}
                            className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                              selectedArticle === article.id && selectedSection === section.id
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            {article.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          )}
        </div>

        {/* Right content area */}
        <div ref={articleContentRef} className="flex-1 overflow-y-auto p-4" data-testid="help-article-content">
          {currentArticle ? (
            /* Article view */
            <div className="max-w-3xl">
              {/* Breadcrumb navigation */}
              <nav aria-label="breadcrumb" className="flex items-center gap-1 text-xs text-gray-400 mb-3">
                <button
                  onClick={() => { setSelectedSection(null); setSelectedArticle(null); }}
                  className="hover:text-blue-600 transition-colors"
                >
                  Help Center
                </button>
                <span>/</span>
                <button
                  onClick={() => {
                    const firstArticle = currentSection?.articles[0];
                    if (firstArticle) setSelectedArticle(firstArticle.id);
                    else setSelectedArticle(null);
                  }}
                  className="hover:text-blue-600 transition-colors"
                >
                  {currentSection?.title}
                </button>
                <span>/</span>
                <span className="text-gray-600 font-medium truncate max-w-xs">{currentArticle.title}</span>
              </nav>

              {/* In this section — quick navigation to other articles */}
              {currentSection && currentSection.articles.length > 1 && (
                <div className="mb-3 bg-white border border-gray-200 rounded-lg px-3 py-2.5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    In this section
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {currentSection.articles.map(article => (
                      <button
                        key={article.id}
                        onClick={() => selectArticle(currentSection.id, article.id)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          article.id === selectedArticle
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-700 border border-transparent'
                        }`}
                      >
                        {article.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {currentArticle.title}
                </h3>
                <div>
                  {renderContent(currentArticle.content)}
                </div>
              </div>
            </div>
          ) : selectedSection ? (
            /* Section overview - show all articles as cards */
            <div className="max-w-3xl">
              {/* Breadcrumb navigation */}
              <nav aria-label="breadcrumb" className="flex items-center gap-1 text-xs text-gray-400 mb-3">
                <button
                  onClick={() => setSelectedSection(null)}
                  className="hover:text-blue-600 transition-colors"
                >
                  Help Center
                </button>
                <span>/</span>
                <span className="text-gray-600 font-medium">{currentSection?.title}</span>
              </nav>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span>{currentSection?.icon}</span>
                  <span>{currentSection?.title}</span>
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {currentSection?.articles.length} article{currentSection?.articles.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentSection?.articles.map(article => (
                  <button
                    key={article.id}
                    onClick={() => selectArticle(selectedSection, article.id)}
                    className="text-left bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all group"
                  >
                    <h4 className="text-sm font-medium text-gray-800 group-hover:text-blue-700 transition-colors">
                      {article.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {article.content?.[0]?.text || ''}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Welcome/landing state */
            <div className="max-w-3xl">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 text-center">
                <div className="text-4xl mb-3">📚</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Welcome to the Help Center
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Find answers to your questions about using TreeGenie. Select a topic from the left or use the search bar to find what you need.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {filteredSections.slice(0, 6).map(section => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setSelectedSection(section.id);
                        setExpandedSections(prev => ({ ...prev, [section.id]: true }));
                      }}
                      className="flex flex-col items-center p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all"
                    >
                      <span className="text-2xl mb-1">{section.icon}</span>
                      <span className="text-xs font-medium text-gray-700">{section.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
